import { useEffect, useState } from "react"
import classNames from "classnames"
import styled from "styled-components"
import { v4 as uuidv4 } from "uuid"

import { useLazyGetMonitoringQuery } from "store/api/widgetsApi"

import { WidgetLayout } from "layouts/WidgetLayout"
import { ChartEmptyState } from "components/chart/ChartEmptyState"
import { GaugeSkeleton } from "components/chart/ChartEmptyState/skeletons"
import { RainbowChart } from "components/common/RainbowChart"
import { Spinner } from "components/common/Spinner"
import { Tooltip } from "components/common/Tooltip"

import styles from "./index.module.scss"

const StyledChartEmptyState = styled(ChartEmptyState)`
  margin-top: -65px !important;
`

const initialState = {
  installedDevices: 0,
  installedDevicesInPercent: 0,
  purchasedDevices: 0,
}

interface Props {
  updateIndex: number
  variant?: "default" | "enterprise"
}

export const WidgetMonitoring = ({ updateIndex, variant = "default" }: Props) => {
  const [triggerGetMonitoring, { isFetching, data: monitoringData }] = useLazyGetMonitoringQuery()
  const [widgetData, setWidgetData] = useState(initialState)
  const isEnterprise = variant === "enterprise"

  useEffect(() => {
    const fetchMonitoringData = async () => {
      try {
        const res = await triggerGetMonitoring()
        if (res?.data) {
          const data = res.data.data

          const installedDevices = data?.installed_devices ?? 0
          const purchasedDevices = data?.purchased_devices ?? 0

          setWidgetData({
            installedDevices,
            purchasedDevices,
            installedDevicesInPercent: purchasedDevices === 0 ? 0 : (installedDevices / purchasedDevices) * 100,
          })
        }
      } catch (error) {
        console.error(error)
      }
    }

    fetchMonitoringData()
  }, [updateIndex])

  const hasNoData =
    !isFetching && !!monitoringData?.data && widgetData.purchasedDevices + widgetData.installedDevices === 0

  return (
    <WidgetLayout
      title="Monitoring"
      className={classNames(styles.widgetMembership, { [styles.enterpriseWidget]: isEnterprise })}
    >
      <div className={classNames(styles.widgetContent, { [styles.disabled]: isFetching })}>
        {hasNoData ? (
          <StyledChartEmptyState skeleton={<GaugeSkeleton />} />
        ) : (
          <>
            <div className={classNames(styles.chartWrapper, { [styles.enterpriseChartWrapper]: isEnterprise })}>
              <RainbowChart
                colors={["#43D2E6", "#E2E8F0"]}
                values={[widgetData.installedDevices, widgetData.purchasedDevices - widgetData.installedDevices]}
                className={classNames(styles.chart, { [styles.enterpriseChart]: isEnterprise })}
                options={{ cutout: "65%" }}
              />
            </div>
            <div className={classNames(styles.widgetMainValue, { [styles.enterpriseMainValue]: isEnterprise })}>
              <Tooltip id={uuidv4()} text="INSTALLED">
                % of purchased and installed thermostats <br />
                (Missing and Warranty thermostats are excluded)
              </Tooltip>
              <div className={styles.widgetValueWrapper}>{widgetData.installedDevicesInPercent.toFixed()}%</div>
            </div>
            <footer className={styles.widgetFooter}>
              <div className={classNames(styles.widgetFooterItem)}>
                <Tooltip id={uuidv4()} text="Purchased">
                  Thermostat(s) under Missing, Transferring and
                  <br /> Warranty status are excluded from the count.
                </Tooltip>
                <div className={styles.widgetValueWrapper}>{widgetData.purchasedDevices}</div>
              </div>
              <div className={classNames(styles.widgetFooterItem)}>
                <span className={styles.widgetNote}>Installed</span>
                <div className={styles.widgetValueWrapper}>{widgetData.installedDevices}</div>
              </div>
            </footer>
          </>
        )}
        {isFetching && (
          <div className={styles.widgetLoader}>
            <Spinner />
          </div>
        )}
      </div>
    </WidgetLayout>
  )
}
